// Settings → Users (CR_Manager_vs_User_role §5.4): a manager invites warehouse staff and project users and sets
// what each sees — warehouses All / Selected (warehouse staff only), projects None / All / Selected — and
// deactivates them. A project user never gets a warehouse; the server refuses it too. Access is saved in one
// transaction by set_member_access; managers are FleetSpoke's to create (super admins get the Manager role) and to
// move to staff (a super admin on a support visit gets "Change role" on a manager's row — the one path, D6a).
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { placeKind } from '@fleetspoke/core/places';
import type { Scope } from '@fleetspoke/core/types';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../auth/session';
import { LastActive } from '../../components/LastActive';
import { Empty, Loading, LoadError, Page } from '../../components/Page';
import { useProjects } from '../../data/billing';
import { keys, useSites } from '../../data/queries';
import { supabase } from '../../lib/supabase';

type StaffRole = 'warehouse_staff' | 'project_user';
type MemberRole = 'manager' | StaffRole;

interface Member {
  id: string;
  user_id: string;
  role: MemberRole;
  warehouse_scope: Scope;
  project_scope: Scope;
  is_active: boolean;
  full_name: string | null;
  phone: string | null;
  /** direct grants: selected warehouses and locations without a project */
  site_ids: string[];
  project_ids: string[];
  /** user_activity — stamped by whoami while the app is open */
  last_active_at: string | null;
}

export interface Access {
  role: StaffRole;
  warehouseScope: Scope;
  projectScope: Scope;
  siteIds: string[];
  projectIds: string[];
}

function useMembers() {
  return useQuery({
    queryKey: keys.members,
    queryFn: async () => {
      const [m, p, s, pm, a] = await Promise.all([
        supabase()
          .from('memberships')
          .select('id, user_id, role, warehouse_scope, project_scope, is_active')
          .order('created_at'),
        supabase().from('profiles').select('user_id, full_name, phone'),
        supabase().from('site_memberships').select('user_id, site_id'),
        supabase().from('project_memberships').select('user_id, project_id'),
        supabase().from('user_activity').select('user_id, last_active_at'),
      ]);
      const failed = m.error ?? p.error ?? s.error ?? pm.error ?? a.error;
      if (failed) throw new Error(failed.message);
      const profiles = new Map((p.data ?? []).map((x) => [x.user_id, x]));
      const activity = new Map((a.data ?? []).map((x) => [x.user_id, x.last_active_at]));
      return (m.data ?? []).map((row) => ({
        ...row,
        full_name: profiles.get(row.user_id)?.full_name ?? null,
        phone: profiles.get(row.user_id)?.phone ?? null,
        site_ids: (s.data ?? []).filter((x) => x.user_id === row.user_id).map((x) => x.site_id),
        project_ids: (pm.data ?? [])
          .filter((x) => x.user_id === row.user_id)
          .map((x) => x.project_id),
        last_active_at: activity.get(row.user_id) ?? null,
      })) as Member[];
    },
  });
}

/** The same rules the server applies (write_member_access): null when the selection is complete. */
export function accessProblem(a: Access, warehouseIds: Set<string>): string | null {
  const warehouses = a.siteIds.filter((id) => warehouseIds.has(id)).length;
  const locations = a.siteIds.length - warehouses;
  if (a.role === 'project_user') {
    if (warehouses) return 'settings.users.err_project_user_warehouse';
    if (a.projectScope === 'selected' && a.projectIds.length + locations === 0)
      return 'settings.users.err_no_project';
    return null;
  }
  if (a.warehouseScope === 'selected' && warehouses === 0) return 'settings.users.err_no_warehouse';
  if (a.projectScope === 'selected' && a.projectIds.length + locations === 0)
    return 'settings.users.err_no_project';
  return null;
}

/** What goes to the server: a project user drops warehouses; "all" and "none" need no rows. */
export function accessPayload(a: Access, warehouseIds: Set<string>) {
  const warehouseScope: Scope = a.role === 'project_user' ? 'none' : a.warehouseScope;
  const siteIds = a.siteIds.filter((id) =>
    warehouseIds.has(id) ? warehouseScope === 'selected' : a.projectScope === 'selected',
  );
  return {
    role: a.role,
    warehouse_scope: warehouseScope,
    project_scope: a.projectScope,
    site_ids: siteIds,
    project_ids: a.projectScope === 'selected' ? a.projectIds : [],
  };
}

function Ticks({
  items,
  chosen,
  onChange,
}: {
  items: { id: string; label: string }[];
  chosen: string[];
  onChange(ids: string[]): void;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', pl: 3.5 }}>
      {items.map((i) => (
        <FormControlLabel
          key={i.id}
          label={i.label}
          control={
            <Checkbox
              size="small"
              checked={chosen.includes(i.id)}
              onChange={(e) =>
                onChange(e.target.checked ? [...chosen, i.id] : chosen.filter((x) => x !== i.id))
              }
            />
          }
        />
      ))}
    </Box>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant="subtitle2">{label}</Typography>
      {children}
    </Box>
  );
}

/** Role + what the person sees (§5.4). "Other locations" only shows what a person already holds. */
function AccessFields({
  value,
  onChange,
  showOther,
}: {
  value: Access;
  onChange(a: Access): void;
  showOther: boolean;
}) {
  const { t } = useTranslation();
  const sites = useSites();
  const projects = useProjects();
  const [showCompleted, setShowCompleted] = useState(false);
  const set = (patch: Partial<Access>) => onChange({ ...value, ...patch });
  const warehouses = (sites.data ?? []).filter(
    (s) => placeKind(s.type) === 'warehouse' && (s.is_active || value.siteIds.includes(s.site_id)),
  );
  const other = (sites.data ?? []).filter(
    (s) => placeKind(s.type) === 'location' && !s.project_id && value.siteIds.includes(s.site_id),
  );
  const closed = (projects.data ?? []).filter(
    (p) =>
      (p.status === 'completed' || p.status === 'cancelled') && !value.projectIds.includes(p.id),
  );
  const shownProjects = (projects.data ?? []).filter((p) => showCompleted || !closed.includes(p));
  const locationsOf = (projectId: string) =>
    (sites.data ?? []).filter((s) => s.project_id === projectId && placeKind(s.type) === 'location')
      .length;
  const staff = value.role === 'warehouse_staff';
  const warehouseIds = new Set(
    (sites.data ?? []).filter((s) => placeKind(s.type) === 'warehouse').map((s) => s.site_id),
  );
  return (
    <Stack spacing={1.5} data-testid="access-fields">
      <Row label={t('settings.users.role')}>
        <RadioGroup
          row
          value={value.role}
          onChange={(e) => {
            const role = e.target.value as StaffRole;
            set(
              role === 'project_user'
                ? {
                    role,
                    warehouseScope: 'none',
                    projectScope: value.projectScope === 'none' ? 'selected' : value.projectScope,
                    // a project user never has warehouses: their ticks go with the switch
                    siteIds: value.siteIds.filter((id) => !warehouseIds.has(id)),
                  }
                : {
                    role,
                    warehouseScope: value.warehouseScope === 'none' ? 'all' : value.warehouseScope,
                  },
            );
          }}
        >
          <FormControlLabel
            value="warehouse_staff"
            control={<Radio />}
            label={t('settings.users.role_warehouse_staff')}
          />
          <FormControlLabel
            value="project_user"
            control={<Radio />}
            label={t('settings.users.role_project_user')}
          />
        </RadioGroup>
        <Typography variant="caption" color="text.secondary">
          {staff ? t('settings.users.role_help_warehouse') : t('settings.users.role_help_project')}
        </Typography>
      </Row>
      {staff ? (
        <Row label={t('nav.warehouses')}>
          <RadioGroup
            row
            value={value.warehouseScope}
            onChange={(e) => set({ warehouseScope: e.target.value as Scope })}
          >
            <FormControlLabel
              value="all"
              control={<Radio />}
              label={t('settings.users.scope_all_warehouses')}
            />
            <FormControlLabel
              value="selected"
              control={<Radio />}
              label={t('settings.users.scope_selected')}
            />
          </RadioGroup>
          {value.warehouseScope === 'selected' ? (
            <Ticks
              items={warehouses.map((s) => ({ id: s.site_id, label: s.name }))}
              chosen={value.siteIds}
              onChange={(ids) =>
                set({
                  siteIds: [
                    ...value.siteIds.filter((id) => !warehouses.some((w) => w.site_id === id)),
                    ...ids,
                  ],
                })
              }
            />
          ) : null}
        </Row>
      ) : null}
      <Row label={t('nav.projects')}>
        <RadioGroup
          row
          value={value.projectScope}
          onChange={(e) => set({ projectScope: e.target.value as Scope })}
        >
          {staff ? (
            <FormControlLabel
              value="none"
              control={<Radio />}
              label={t('settings.users.scope_none')}
            />
          ) : null}
          <FormControlLabel
            value="all"
            control={<Radio />}
            label={t('settings.users.scope_all_projects')}
          />
          <FormControlLabel
            value="selected"
            control={<Radio />}
            label={t('settings.users.scope_selected')}
          />
        </RadioGroup>
        {value.projectScope === 'selected' ? (
          <>
            {shownProjects.length ? (
              <Ticks
                items={shownProjects.map((p) => ({
                  id: p.id,
                  label: `${p.name} · ${t('projects.n_locations', { count: locationsOf(p.id) })}`,
                }))}
                chosen={value.projectIds}
                onChange={(ids) => set({ projectIds: ids })}
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ pl: 3.5 }}>
                {t('projects.none')}
              </Typography>
            )}
            {closed.length && !showCompleted ? (
              <Button size="small" sx={{ ml: 3 }} onClick={() => setShowCompleted(true)}>
                {t('settings.users.show_completed', { count: closed.length })}
              </Button>
            ) : null}
          </>
        ) : null}
      </Row>
      {showOther && other.length ? (
        <Row label={t('settings.users.other_locations')}>
          <Ticks
            items={other.map((s) => ({ id: s.site_id, label: s.name }))}
            chosen={value.siteIds}
            onChange={(ids) =>
              set({
                siteIds: [
                  ...value.siteIds.filter((id) => !other.some((o) => o.site_id === id)),
                  ...ids,
                ],
              })
            }
          />
          <Typography variant="caption" color="text.secondary" sx={{ pl: 3.5 }}>
            {t('settings.users.other_locations_help')}
          </Typography>
        </Row>
      ) : null}
    </Stack>
  );
}

const useWarehouseIds = () => {
  const sites = useSites();
  return new Set(
    (sites.data ?? []).filter((s) => placeKind(s.type) === 'warehouse').map((s) => s.site_id),
  );
};

const NEW_ACCESS: Access = {
  role: 'warehouse_staff',
  warehouseScope: 'all',
  projectScope: 'none',
  siteIds: [],
  projectIds: [],
};

function InviteDialog({ onClose }: { onClose(): void }) {
  const { t } = useTranslation();
  const { me } = useSession();
  const queryClient = useQueryClient();
  const warehouseIds = useWarehouseIds();
  const [form, setForm] = useState({
    full_name: '',
    phone: '',
    email: '',
    role: 'staff' as 'staff' | 'manager',
    locale: 'en' as 'en' | 'ms' | 'zh',
  });
  const [access, setAccess] = useState<Access>(NEW_ACCESS);
  const problem = form.role === 'manager' ? null : accessProblem(access, warehouseIds);
  const invite = useMutation({
    mutationFn: async () => {
      const { error } = await supabase().functions.invoke('invite-user', {
        body: {
          company_id: me!.company!.id,
          phone: form.phone,
          email: form.email.trim() || undefined,
          full_name: form.full_name.trim() || undefined,
          locale: form.locale,
          ...(form.role === 'manager' ? { role: 'manager' } : accessPayload(access, warehouseIds)),
        },
      });
      if (error) {
        const code =
          error instanceof FunctionsHttpError
            ? ((await error.context.json().catch(() => ({}))) as { error?: string }).error
            : undefined;
        const messages: Record<string, string> = {
          member_of_another_account: t('settings.users.err_member_elsewhere'),
          super_admin: t('settings.users.err_super_admin'),
          invalid_phone: t('settings.users.err_invalid_phone'),
          invalid_sites: t('settings.users.err_invalid_sites'),
          forbidden: t('common.not_allowed'),
        };
        throw new Error(messages[code ?? ''] ?? t('common.error_generic'));
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.members });
    },
  });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    invite.mutate();
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={submit}>
        <DialogTitle>{t('settings.users.invite')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label={t('settings.users.name')}
              value={form.full_name}
              onChange={set('full_name')}
              autoFocus
            />
            <TextField
              label={t('settings.users.phone')}
              value={form.phone}
              onChange={set('phone')}
              required
              helperText={t('settings.users.phone_help')}
            />
            <TextField
              label={`${t('settings.users.email')} (${t('common.optional')})`}
              value={form.email}
              onChange={set('email')}
              type="email"
            />
            <Stack direction="row" spacing={2}>
              {me?.platform_admin ? (
                <TextField
                  select
                  label={t('settings.users.kind')}
                  value={form.role}
                  onChange={set('role')}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="staff">{t('settings.users.kind_staff')}</MenuItem>
                  <MenuItem value="manager">{t('settings.users.role_manager')}</MenuItem>
                </TextField>
              ) : null}
              <TextField
                select
                label={t('settings.users.locale')}
                value={form.locale}
                onChange={set('locale')}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="ms">Bahasa Melayu</MenuItem>
                <MenuItem value="zh">中文</MenuItem>
              </TextField>
            </Stack>
            {form.role === 'staff' ? (
              <AccessFields value={access} onChange={setAccess} showOther={false} />
            ) : null}
            {problem ? <Alert severity="info">{t(problem)}</Alert> : null}
            {invite.error && <Alert severity="error">{(invite.error as Error).message}</Alert>}
            {invite.isSuccess && <Alert severity="success">{t('settings.users.invited')}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.close')}</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={invite.isPending || !form.phone.trim() || invite.isSuccess || !!problem}
          >
            {t('settings.users.invite')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function AccessDialog({ member, onClose }: { member: Member; onClose(): void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const warehouseIds = useWarehouseIds();
  const [access, setAccess] = useState<Access>({
    role: member.role === 'project_user' ? 'project_user' : 'warehouse_staff',
    warehouseScope: member.warehouse_scope,
    projectScope: member.project_scope,
    siteIds: member.site_ids,
    projectIds: member.project_ids,
  });
  const problem = accessProblem(access, warehouseIds);
  const save = useMutation({
    mutationFn: async () => {
      const p = accessPayload(access, warehouseIds);
      const { error } = await supabase().rpc('set_member_access', {
        p_membership: member.id,
        p_role: p.role,
        p_warehouse_scope: p.warehouse_scope,
        p_project_scope: p.project_scope,
        p_site_ids: p.site_ids,
        p_project_ids: p.project_ids,
      });
      if (error)
        throw new Error(
          error.code === '22023'
            ? t('settings.users.err_invalid_sites')
            : error.code === '42501'
              ? t('common.not_allowed')
              : error.message,
        );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.members });
      onClose();
    },
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{member.full_name ?? member.phone}</DialogTitle>
      <DialogContent dividers>
        {member.role === 'manager' ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('settings.users.demote_help')}
          </Alert>
        ) : null}
        <AccessFields value={access} onChange={setAccess} showOther />
        {problem ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t(problem)}
          </Alert>
        ) : null}
        {save.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(save.error as Error).message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={save.isPending || !!problem}
          onClick={() => save.mutate()}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function UsersPage() {
  const { t } = useTranslation();
  const { me } = useSession();
  const queryClient = useQueryClient();
  const members = useMembers();
  const sites = useSites();
  const projects = useProjects();
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const siteName = new Map((sites.data ?? []).map((s) => [s.site_id, s.name]));
  const projectName = new Map((projects.data ?? []).map((p) => [p.id, p.name]));
  const toggle = useMutation({
    mutationFn: async (m: Member) => {
      const { error, data } = await supabase()
        .from('memberships')
        .update({ is_active: !m.is_active })
        .eq('id', m.id)
        .select('id');
      if (error || !data?.length) throw new Error(error?.message ?? t('common.not_allowed'));
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.members }),
  });
  const staff = (m: Member) => m.role === 'warehouse_staff' || m.role === 'project_user';
  const editable = (m: Member) => m.user_id !== me?.user_id && (staff(m) || !!me?.platform_admin);
  // "All warehouses · KL Tower (project)" — what the person sees, in one line
  const reach = (m: Member) => {
    if (m.role === 'manager') return t('settings.users.access_everything');
    const parts: string[] = [];
    if (m.role === 'warehouse_staff')
      parts.push(
        m.warehouse_scope === 'all'
          ? t('settings.users.scope_all_warehouses')
          : m.site_ids
              .filter(
                (id) =>
                  siteName.has(id) &&
                  (sites.data ?? []).some(
                    (s) => s.site_id === id && placeKind(s.type) === 'warehouse',
                  ),
              )
              .map((id) => siteName.get(id))
              .join(', '),
      );
    if (m.project_scope === 'all') parts.push(t('settings.users.scope_all_projects'));
    else if (m.project_scope === 'selected')
      parts.push(
        ...m.project_ids.map(
          (id) => `${projectName.get(id) ?? '…'} (${t('sites.project').toLowerCase()})`,
        ),
        ...m.site_ids
          .filter((id) =>
            (sites.data ?? []).some((s) => s.site_id === id && placeKind(s.type) === 'location'),
          )
          .map((id) => siteName.get(id) ?? '…'),
      );
    return parts.filter(Boolean).join(' · ') || '—';
  };

  return (
    <Page
      title={t('settings.users.title')}
      actions={
        <Button variant="contained" onClick={() => setInviting(true)}>
          {t('settings.users.invite')}
        </Button>
      }
    >
      {toggle.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(toggle.error as Error).message}
        </Alert>
      )}
      {members.isLoading ? (
        <Loading />
      ) : members.error ? (
        <LoadError error={members.error} />
      ) : !members.data?.length ? (
        <Empty>—</Empty>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('settings.users.name')}</TableCell>
                <TableCell>{t('settings.users.phone')}</TableCell>
                <TableCell>{t('settings.users.role')}</TableCell>
                <TableCell>{t('settings.users.site_access')}</TableCell>
                <TableCell>{t('settings.users.last_active')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {members.data.map((m) => (
                <TableRow
                  key={m.id}
                  sx={{ opacity: m.is_active ? 1 : 0.5 }}
                  data-testid="member-row"
                >
                  <TableCell>
                    {m.full_name ?? '—'}
                    {!m.is_active && (
                      <Chip
                        size="small"
                        sx={{ ml: 1 }}
                        label={t('settings.users.status_inactive')}
                      />
                    )}
                  </TableCell>
                  <TableCell>{m.phone ?? '—'}</TableCell>
                  <TableCell>{t(`settings.users.role_${m.role}`)}</TableCell>
                  <TableCell>{reach(m)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <LastActive
                      activeAt={m.last_active_at}
                      timeZone={me?.company?.timezone ?? 'Asia/Kuala_Lumpur'}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {editable(m) && (staff(m) || m.is_active) && (
                      <Button size="small" onClick={() => setEditing(m)}>
                        {staff(m)
                          ? t('settings.users.site_access')
                          : t('settings.users.change_role')}
                      </Button>
                    )}
                    {editable(m) && (
                      <Button
                        size="small"
                        color={m.is_active ? 'error' : 'primary'}
                        onClick={() => toggle.mutate(m)}
                      >
                        {m.is_active
                          ? t('settings.users.deactivate')
                          : t('settings.users.reactivate')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {inviting && <InviteDialog onClose={() => setInviting(false)} />}
      {editing && <AccessDialog member={editing} onClose={() => setEditing(null)} />}
    </Page>
  );
}
